import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { RadioGroupCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';

import { AusType } from 'constants/loanApplication';
import { ItemLabelPlacement } from 'constants/ui';
import useLoanId from 'hooks/useLoanId';

import { selectAusSummary } from 'store/loanApplications/selectors';

import {
  ausTypeWrapperStyles,
  getCellStyles,
  getHeadCellStyles,
  radioGroupCustomStyles,
  tableContainerStyles,
  tableStyles,
  titleStyles,
  wrapperStyles,
} from './AusComparison.styles';
import { AusComparisonTestIds } from './AusComparison.testIds';
import { ausTypeOptions, columns, getRows } from './AusComparison.utils';

const ausTypeBoxStyles = { ...ausTypeWrapperStyles, ...radioGroupCustomStyles };

const AusComparison = () => {
  const ausSummary = useSelector(selectAusSummary);

  const loanId = useLoanId();

  const rows = useMemo(() => {
    if (!ausSummary) {
      return [];
    }

    return getRows({ ausSummary, loanId });
  }, [ausSummary, loanId]);

  return (
    <Box className="aus-comparison-wrapper" sx={wrapperStyles}>
      <Typography className="title" sx={titleStyles} variant="regular14">
        AUS Comparison
      </Typography>
      <TableContainer className="table-container" sx={tableContainerStyles}>
        <Table className="table" sx={tableStyles}>
          <TableHead className="table-head">
            <TableRow className="table-row">
              {columns.map(({ width, content, key }) => (
                <TableCell
                  data-truncated
                  className="table-cell"
                  key={key}
                  sx={getHeadCellStyles({ width })}
                >
                  {content}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody className="table-body">
            {rows.map(({ key, entries }) => (
              <TableRow className="table-row" data-row-key={key} key={key}>
                {entries.map(({ content, key, customStyles }) => (
                  <TableCell className="table-cell" key={key} sx={getCellStyles(customStyles)}>
                    <Box className="table-cell__content_box">{content}</Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box className="aus-type-wrapper" sx={ausTypeBoxStyles}>
        <RadioGroupCDS
          disabled
          row
          dataTestId={AusComparisonTestIds.AUS_TYPE_RADIO}
          labelPlacement={ItemLabelPlacement.END}
          name="ausType"
          options={ausTypeOptions}
          value={ausSummary?.selectedAusType === 1 ? AusType.LP : AusType.DU}
        />
      </Box>
    </Box>
  );
};

export default AusComparison;
